import type { FbContentType, LinkPreview, PostMedia } from '../../../types/publishing';
import { BrandCircle, PostMediaView } from './PostMediaView';
import { LinkifiedText } from './LinkifiedText';
import { collageTiles, facebookCollageApplies } from '../../../lib/publishing/facebookCollage';

// Publishing preview — Facebook. Feed (default) / Story / Reel. Modelled on the
// ad-preview Feed/Story mockups (slate chrome), driven by the effective caption +
// media for this platform.

export function FacebookPreview({
  pageName,
  content,
  media,
  contentType,
  firstComment,
  linkPreview = null,
}: {
  pageName: string;
  content: string;
  media: PostMedia[];
  contentType: FbContentType;
  firstComment: string | null;
  // The predicted link card for the caption's first link (feed post, no media
  // — see src/lib/publishing/linkCard.ts). null = no card, which is also what
  // any fetch failure produces: a broken card is worse than none.
  linkPreview?: LinkPreview | null;
}) {
  if (contentType === 'story') {
    return <StoryFrame pageName={pageName} media={media} />;
  }
  const isReel = contentType === 'reel';
  return (
    <article className="mx-auto max-w-[420px] w-full rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
      <header className="flex items-center gap-3 px-4 py-3">
        <BrandCircle name={pageName} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-900 truncate">{pageName}</p>
          <p className="text-xs text-slate-500">{isReel ? 'Reel · Just now' : 'Just now · 🌐'}</p>
        </div>
        <span className="text-slate-400" aria-hidden>⋯</span>
      </header>
      {content.trim() !== '' && (
        <p className="px-4 pb-3 text-sm text-slate-800 whitespace-pre-wrap">
          <LinkifiedText text={content} />
        </p>
      )}
      {/* A Reel is a fixed 9:16 surface (Meta crops to fill), so it keeps the
          fixed frame + crop; the true feed placement sizes to the image's own
          ratio (natural — no fixed box, no letterbox bars). The feed aspectClass
          is only the empty-state placeholder shape. */}
      {linkPreview && media.length === 0 ? (
        <LinkCard preview={linkPreview} />
      ) : facebookCollageApplies(media, isReel ? 'reel' : 'feed') ? (
        <FacebookCollage media={media} />
      ) : (
        <PostMediaView
          media={media}
          aspectClass={isReel ? 'aspect-[9/16]' : 'aspect-[1.91/1]'}
          fit={isReel ? 'cover' : 'natural'}
        />
      )}
      <footer className="flex items-center justify-around border-t border-slate-100 px-4 py-2 text-xs text-slate-500">
        <span aria-hidden>👍 Like</span>
        <span aria-hidden>💬 Comment</span>
        <span aria-hidden>↗ Share</span>
      </footer>
      {firstComment && firstComment.trim() !== '' && (
        <div className="border-t border-slate-100 px-4 py-2 flex items-start gap-2">
          <BrandCircle name={pageName} />
          <div className="rounded-2xl bg-slate-100 px-3 py-1.5 text-xs text-slate-700">
            <span className="font-semibold">{pageName}</span> <LinkifiedText text={firstComment} />
          </div>
        </div>
      )}
    </article>
  );
}

// Several feed photos as Facebook's collage — a PREDICTION (see
// src/lib/publishing/facebookCollage.ts, which decides every rule). Tiles come
// in media-array order, so the first photo is the large tile and a reorder in
// the composer visibly changes the collage.
function FacebookCollage({ media }: { media: PostMedia[] }) {
  const { tiles, overflow } = collageTiles(media);
  const n = tiles.length;
  // 2 → side by side; 3 → tall left + two stacked right; 4 → 2×2.
  const frame = n === 2 ? 'aspect-[2/1] grid-cols-2' : 'aspect-square grid-cols-2 grid-rows-2';
  return (
    <div data-testid="fb-collage" className={`grid ${frame} w-full gap-0.5 bg-white`}>
      {tiles.map((m, i) => {
        const last = i === n - 1;
        return (
          <div
            key={`${m.url}-${i}`}
            className={`relative overflow-hidden bg-slate-200 ${n === 3 && i === 0 ? 'row-span-2' : ''}`}
          >
            <img
              src={m.thumbnailUrl ?? m.url}
              alt={m.altText || m.filename || 'post media'}
              loading="lazy"
              className="h-full w-full object-cover"
            />
            {last && overflow > 0 && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50 text-2xl font-semibold text-white">
                +{overflow}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// The Facebook link card as a PREDICTION. Facebook runs its own scraper and may
// pick a different image or title, so the card says so. Title/description are
// scraped page text: rendered as React text (escaped), never as HTML.
function LinkCard({ preview }: { preview: LinkPreview }) {
  return (
    <div>
      <a
        href={preview.url}
        target="_blank"
        rel="noopener noreferrer"
        className="block border-y border-slate-200 bg-slate-50 hover:bg-slate-100"
      >
        {preview.imageUrl && (
          <img
            src={preview.imageUrl}
            alt=""
            className="aspect-[1.91/1] w-full object-cover"
            referrerPolicy="no-referrer"
          />
        )}
        <div className="px-3 py-2">
          <p className="text-[11px] uppercase tracking-wide text-slate-500">{preview.domain}</p>
          <p className="line-clamp-2 text-sm font-semibold text-slate-900">{preview.title}</p>
          {preview.description && (
            <p className="line-clamp-1 text-xs text-slate-500">{preview.description}</p>
          )}
        </div>
      </a>
      <p className="px-4 pt-1 text-[10px] text-slate-400">
        Preview of the link card. Facebook may show a different image or title.
      </p>
    </div>
  );
}

function StoryFrame({ pageName, media }: { pageName: string; media: PostMedia[] }) {
  return (
    <div className="mx-auto max-w-[260px] w-full">
      <div className="relative rounded-xl overflow-hidden border border-slate-200 bg-black">
        <PostMediaView media={media} aspectClass="aspect-[9/16]" />
        <div className="absolute top-0 inset-x-0 p-3 flex items-center gap-2 bg-gradient-to-b from-black/50 to-transparent">
          <BrandCircle name={pageName} />
          <span className="text-xs font-semibold text-white truncate">{pageName}</span>
        </div>
      </div>
      <p className="mt-1 text-center text-xs text-slate-500">Facebook Story</p>
    </div>
  );
}
